/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  InfiniteData,
  InvalidateQueryFilters,
  QueryClient,
  QueryKey,
  useInfiniteQuery,
  UseInfiniteQueryResult,
  useQuery,
  UseQueryResult,
  useSuspenseInfiniteQuery,
  UseSuspenseInfiniteQueryResult,
  useSuspenseQuery,
  UseSuspenseQueryResult,
} from "@tanstack/react-query";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  ListSkillLinksRequest,
  ListSkillLinksSecurity,
  TargetKind,
} from "../models/operations/listskilllinks.js";
import { useGramContext } from "./_context.js";
import {
  InfiniteQueryHookOptions,
  QueryHookOptions,
  SuspenseInfiniteQueryHookOptions,
  SuspenseQueryHookOptions,
  TupleToPrefixes,
} from "./_types.js";
import {
  buildSkillLinksInfiniteQuery,
  buildSkillLinksQuery,
  prefetchSkillLinks,
  prefetchSkillLinksInfinite,
  queryKeySkillLinks,
  queryKeySkillLinksInfinite,
  SkillLinksInfiniteQueryData,
  SkillLinksPageParams,
  SkillLinksQueryData,
} from "./skillLinks.core.js";
export {
  buildSkillLinksInfiniteQuery,
  buildSkillLinksQuery,
  prefetchSkillLinks,
  prefetchSkillLinksInfinite,
  queryKeySkillLinks,
  queryKeySkillLinksInfinite,
  type SkillLinksInfiniteQueryData,
  type SkillLinksPageParams,
  type SkillLinksQueryData,
};

export type SkillLinksQueryError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * listLinks skills
 *
 * @remarks
 * List the current project's live skill links, limited to MCP surfaces the caller can read. Requires the skills-over-mcp rollout for the organization.
 */
export function useSkillLinks(
  request?: ListSkillLinksRequest | undefined,
  security?: ListSkillLinksSecurity | undefined,
  options?: QueryHookOptions<SkillLinksQueryData, SkillLinksQueryError>,
): UseQueryResult<SkillLinksQueryData, SkillLinksQueryError> {
  const client = useGramContext();
  return useQuery({
    ...buildSkillLinksQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * listLinks skills
 *
 * @remarks
 * List the current project's live skill links, limited to MCP surfaces the caller can read. Requires the skills-over-mcp rollout for the organization.
 */
export function useSkillLinksSuspense(
  request?: ListSkillLinksRequest | undefined,
  security?: ListSkillLinksSecurity | undefined,
  options?: SuspenseQueryHookOptions<SkillLinksQueryData, SkillLinksQueryError>,
): UseSuspenseQueryResult<SkillLinksQueryData, SkillLinksQueryError> {
  const client = useGramContext();
  return useSuspenseQuery({
    ...buildSkillLinksQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * listLinks skills
 *
 * @remarks
 * List the current project's live skill links, limited to MCP surfaces the caller can read. Requires the skills-over-mcp rollout for the organization.
 */
export function useSkillLinksInfinite(
  request?: ListSkillLinksRequest | undefined,
  security?: ListSkillLinksSecurity | undefined,
  options?: InfiniteQueryHookOptions<
    SkillLinksInfiniteQueryData,
    SkillLinksQueryError
  >,
): UseInfiniteQueryResult<
  InfiniteData<SkillLinksInfiniteQueryData, SkillLinksPageParams>,
  SkillLinksQueryError
> {
  const client = useGramContext();
  return useInfiniteQuery<
    SkillLinksInfiniteQueryData,
    SkillLinksQueryError,
    InfiniteData<SkillLinksInfiniteQueryData, SkillLinksPageParams>,
    QueryKey,
    SkillLinksPageParams
  >({
    ...buildSkillLinksInfiniteQuery(
      client,
      request,
      security,
      options,
    ),
    initialPageParam: options?.initialPageParam,
    getNextPageParam: (previousPage) => previousPage["~next"],
    ...options,
  });
}

/**
 * listLinks skills
 *
 * @remarks
 * List the current project's live skill links, limited to MCP surfaces the caller can read. Requires the skills-over-mcp rollout for the organization.
 */
export function useSkillLinksInfiniteSuspense(
  request?: ListSkillLinksRequest | undefined,
  security?: ListSkillLinksSecurity | undefined,
  options?: SuspenseInfiniteQueryHookOptions<
    SkillLinksInfiniteQueryData,
    SkillLinksQueryError
  >,
): UseSuspenseInfiniteQueryResult<
  InfiniteData<SkillLinksInfiniteQueryData, SkillLinksPageParams>,
  SkillLinksQueryError
> {
  const client = useGramContext();
  return useSuspenseInfiniteQuery<
    SkillLinksInfiniteQueryData,
    SkillLinksQueryError,
    InfiniteData<SkillLinksInfiniteQueryData, SkillLinksPageParams>,
    QueryKey,
    SkillLinksPageParams
  >({
    ...buildSkillLinksInfiniteQuery(
      client,
      request,
      security,
      options,
    ),
    initialPageParam: options?.initialPageParam,
    getNextPageParam: (previousPage) => previousPage["~next"],
    ...options,
  });
}

export function setSkillLinksData(
  client: QueryClient,
  queryKeyBase: [
    parameters: {
      skillId?: string | undefined;
      mcpServerId?: string | undefined;
      metaMcpServerId?: string | undefined;
      targetKind?: TargetKind | undefined;
      cursor?: string | undefined;
      limit?: number | undefined;
      gramSession?: string | undefined;
      gramKey?: string | undefined;
      gramProject?: string | undefined;
    },
  ],
  data: SkillLinksQueryData,
): SkillLinksQueryData | undefined {
  const key = queryKeySkillLinks(...queryKeyBase);

  return client.setQueryData<SkillLinksQueryData>(key, data);
}

export function invalidateSkillLinks(
  client: QueryClient,
  queryKeyBase: TupleToPrefixes<
    [parameters: {
      skillId?: string | undefined;
      mcpServerId?: string | undefined;
      metaMcpServerId?: string | undefined;
      targetKind?: TargetKind | undefined;
      cursor?: string | undefined;
      limit?: number | undefined;
      gramSession?: string | undefined;
      gramKey?: string | undefined;
      gramProject?: string | undefined;
    }]
  >,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "skills", "listLinks", ...queryKeyBase],
  });
}

export function invalidateAllSkillLinks(
  client: QueryClient,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "skills", "listLinks"],
  });
}
